import React from 'react';
import { API_BASE, toUrl } from '../lib/config';
 
type BaseRow = { id: string; title: string; isActive: boolean; updatedAt: string };

const CompanionVisitorYonetim: React.FC = () => {
  

  const [tab, setTab] = React.useState<'companion' | 'visitor'>('companion');

  // Common
  const [error, setError] = React.useState('');

  // Companions (Refakatçi)
  const [companions, setCompanions] = React.useState<BaseRow[]>([]);
  const [loadingComp, setLoadingComp] = React.useState(false);
  const [qComp, setQComp] = React.useState('');
  const [createCompOpen, setCreateCompOpen] = React.useState(false);
  const [cCompTitle, setCCompTitle] = React.useState('');
  const [cCompActive, setCCompActive] = React.useState(false);
  const [creatingComp, setCreatingComp] = React.useState(false);
  const [editingCompId, setEditingCompId] = React.useState('');
  const [eCompTitle, setECompTitle] = React.useState('');
  const [eCompActive, setECompActive] = React.useState(false);
  const [savingComp, setSavingComp] = React.useState(false);
  const [confirmCompId, setConfirmCompId] = React.useState('');
  const [confirmCompLoading, setConfirmCompLoading] = React.useState(false);

  // Visitors (Ziyaretçi)
  const [visitors, setVisitors] = React.useState<BaseRow[]>([]);
  const [loadingVis, setLoadingVis] = React.useState(false);
  const [qVis, setQVis] = React.useState('');
  const [createVisOpen, setCreateVisOpen] = React.useState(false);
  const [cVisTitle, setCVisTitle] = React.useState('');
  const [cVisActive, setCVisActive] = React.useState(false);
  const [creatingVis, setCreatingVis] = React.useState(false);
  const [editingVisId, setEditingVisId] = React.useState('');
  const [eVisTitle, setEVisTitle] = React.useState('');
  const [eVisActive, setEVisActive] = React.useState(false);
  const [savingVis, setSavingVis] = React.useState(false);
  const [confirmVisId, setConfirmVisId] = React.useState('');
  const [confirmVisLoading, setConfirmVisLoading] = React.useState(false);

  // Fetchers
  const fetchCompanions = React.useCallback(async () => {
    try {
      setLoadingComp(true); setError('');
      const res = await fetch(toUrl('/api/admin/companions'));
      if (!res.ok) throw new Error('Refakatçi listesi alınamadı');
      const data = await res.json();
      setCompanions(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoadingComp(false); }
  }, []);

  const fetchVisitors = React.useCallback(async () => {
    try {
      setLoadingVis(true); setError('');
      const res = await fetch(toUrl('/api/admin/visitors'));
      if (!res.ok) throw new Error('Ziyaretçi listesi alınamadı');
      const data = await res.json();
      setVisitors(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoadingVis(false); }
  }, []);

  React.useEffect(() => { fetchCompanions(); fetchVisitors(); }, [fetchCompanions, fetchVisitors]);

  // Filters
  const filteredComp = React.useMemo(() => {
    const t = qComp.trim().toLowerCase();
    if (!t) return companions;
    return companions.filter(r => (r.title || '').toLowerCase().includes(t));
  }, [companions, qComp]);

  const filteredVis = React.useMemo(() => {
    const t = qVis.trim().toLowerCase();
    if (!t) return visitors;
    return visitors.filter(r => (r.title || '').toLowerCase().includes(t));
  }, [visitors, qVis]);

  // Create handlers
  const resetCreateComp = () => { setCCompTitle(''); setCCompActive(false); };
  const onCreateComp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cCompTitle.trim()) { setError('Lütfen refakatçi başlığı girin'); return; }
    try {
      setCreatingComp(true); setError('');
      const body = { title: cCompTitle.trim(), isActive: !!cCompActive };
      const res = await fetch(toUrl('/api/admin/companions'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Refakatçi oluşturma başarısız');
      resetCreateComp(); setCreateCompOpen(false); await fetchCompanions();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreatingComp(false); }
  };

  // ================= Companion Items (Paragraphs & List Items) =================
  type CompParagraph = { id: string; companionId: string; orderIndex: number; text: string; isActive: boolean; updatedAt: string };
  type CompListItem = { id: string; companionId: string; orderIndex: number; text: string; isActive: boolean; updatedAt: string };

  const [selectedComp, setSelectedComp] = React.useState<BaseRow | null>(null);
  const [compTab, setCompTab] = React.useState<'paragraphs' | 'list'>('paragraphs');

  // Paragraphs state
  const [paras, setParas] = React.useState<CompParagraph[]>([]);
  const [loadingParas, setLoadingParas] = React.useState(false);
  const [cParaText, setCParaText] = React.useState('');
  const [cParaOrder, setCParaOrder] = React.useState<number>(0);
  const [cParaActive, setCParaActive] = React.useState(true);
  const [creatingPara, setCreatingPara] = React.useState(false);
  const [editingParaId, setEditingParaId] = React.useState('');
  const [eParaText, setEParaText] = React.useState('');
  const [eParaOrder, setEParaOrder] = React.useState<number>(0);
  const [eParaActive, setEParaActive] = React.useState(true);
  const [savingPara, setSavingPara] = React.useState(false);
  const [delParaId, setDelParaId] = React.useState('');
  const [delParaLoading, setDelParaLoading] = React.useState(false);

  // List items state
  const [items, setItems] = React.useState<CompListItem[]>([]);
  const [loadingItems, setLoadingItems] = React.useState(false);
  const [cItemText, setCItemText] = React.useState('');
  const [cItemOrder, setCItemOrder] = React.useState<number>(0);
  const [cItemActive, setCItemActive] = React.useState(true);
  const [creatingItem, setCreatingItem] = React.useState(false);
  const [editingItemId, setEditingItemId] = React.useState('');
  const [eItemText, setEItemText] = React.useState('');
  const [eItemOrder, setEItemOrder] = React.useState<number>(0);
  const [eItemActive, setEItemActive] = React.useState(true);
  const [savingItem, setSavingItem] = React.useState(false);
  const [delItemId, setDelItemId] = React.useState('');
  const [delItemLoading, setDelItemLoading] = React.useState(false);

  const loadCompanionChildren = React.useCallback(async (comp: BaseRow) => {
    try {
      setLoadingParas(true); setLoadingItems(true);
      const [r1, r2] = await Promise.all([
        fetch(toUrl(`/api/admin/companions/${comp.id}/paragraphs`)),
        fetch(toUrl(`/api/admin/companions/${comp.id}/list-items`)),
      ]);
      const pjs = r1.ok ? await r1.json() : [];
      const ijs = r2.ok ? await r2.json() : [];
      setParas(pjs || []);
      setItems(ijs || []);
    } catch (e) { /* ignore */ }
    finally { setLoadingParas(false); setLoadingItems(false); }
  }, []);

  const openCompContents = (row: BaseRow) => { setSelectedComp(row); setCompTab('paragraphs'); loadCompanionChildren(row); };

  // Create paragraph
  const resetCreatePara = () => { setCParaText(''); setCParaOrder(0); setCParaActive(true); };
  const onCreatePara = async (e: React.FormEvent) => {
    e.preventDefault(); if (!selectedComp) return;
    if (!cParaText.trim()) { setError('Lütfen paragraf metni girin'); return; }
    try {
      setCreatingPara(true); setError('');
      const body = { companionId: selectedComp.id, orderIndex: cParaOrder || 0, text: cParaText.trim(), isActive: !!cParaActive };
      const res = await fetch(toUrl(`/api/admin/companions/${selectedComp.id}/paragraphs`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Paragraf ekleme başarısız');
      resetCreatePara(); await loadCompanionChildren(selectedComp);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreatingPara(false); }
  };

  const openEditPara = (p: CompParagraph) => { setEditingParaId(p.id); setEParaText(p.text || ''); setEParaOrder(p.orderIndex || 0); setEParaActive(!!p.isActive); };
  const cancelEditPara = () => { setEditingParaId(''); setEParaText(''); setEParaOrder(0); setEParaActive(true); };
  const onSavePara = async (id: string) => {
    if (!eParaText.trim()) { setError('Lütfen paragraf metni girin'); return; }
    try {
      setSavingPara(true); setError('');
      const body: any = { text: eParaText.trim(), orderIndex: eParaOrder || 0, isActive: !!eParaActive };
      const res = await fetch(toUrl(`/api/admin/companion-paragraphs/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Paragraf güncelleme başarısız');
      cancelEditPara(); if (selectedComp) await loadCompanionChildren(selectedComp);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingPara(false); }
  };
  const onDeletePara = (id: string) => setDelParaId(id);
  const confirmDeletePara = async () => {
    if (!delParaId) return;
    try {
      setDelParaLoading(true);
      const res = await fetch(toUrl(`/api/admin/companion-paragraphs/${delParaId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Paragraf silme başarısız');
      setDelParaId(''); if (selectedComp) await loadCompanionChildren(selectedComp);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelParaLoading(false); }
  };

  // Create list item
  const resetCreateItem = () => { setCItemText(''); setCItemOrder(0); setCItemActive(true); };
  const onCreateItem = async (e: React.FormEvent) => {
    e.preventDefault(); if (!selectedComp) return;
    if (!cItemText.trim()) { setError('Lütfen liste maddesi metni girin'); return; }
    try {
      setCreatingItem(true); setError('');
      const body = { companionId: selectedComp.id, orderIndex: cItemOrder || 0, text: cItemText.trim(), isActive: !!cItemActive };
      const res = await fetch(toUrl(`/api/admin/companions/${selectedComp.id}/list-items`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Liste maddesi ekleme başarısız');
      resetCreateItem(); await loadCompanionChildren(selectedComp);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreatingItem(false); }
  };

  const openEditItem = (it: CompListItem) => { setEditingItemId(it.id); setEItemText(it.text || ''); setEItemOrder(it.orderIndex || 0); setEItemActive(!!it.isActive); };
  const cancelEditItem = () => { setEditingItemId(''); setEItemText(''); setEItemOrder(0); setEItemActive(true); };
  const onSaveItem = async (id: string) => {
    if (!eItemText.trim()) { setError('Lütfen liste maddesi metni girin'); return; }
    try {
      setSavingItem(true); setError('');
      const body: any = { text: eItemText.trim(), orderIndex: eItemOrder || 0, isActive: !!eItemActive };
      const res = await fetch(toUrl(`/api/admin/companion-list-items/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Liste maddesi güncelleme başarısız');
      cancelEditItem(); if (selectedComp) await loadCompanionChildren(selectedComp);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingItem(false); }
  };
  const onDeleteItem = (id: string) => setDelItemId(id);
  const confirmDeleteItem = async () => {
    if (!delItemId) return;
    try {
      setDelItemLoading(true);
      const res = await fetch(toUrl(`/api/admin/companion-list-items/${delItemId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Liste maddesi silme başarısız');
      setDelItemId(''); if (selectedComp) await loadCompanionChildren(selectedComp);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelItemLoading(false); }
  };

  const resetCreateVis = () => { setCVisTitle(''); setCVisActive(false); };
  const onCreateVis = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cVisTitle.trim()) { setError('Lütfen ziyaretçi başlığı girin'); return; }
    try {
      setCreatingVis(true); setError('');
      const body = { title: cVisTitle.trim(), isActive: !!cVisActive };
      const res = await fetch(toUrl('/api/admin/visitors'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Ziyaretçi oluşturma başarısız');
      resetCreateVis(); setCreateVisOpen(false); await fetchVisitors();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreatingVis(false); }
  };

  // Edit handlers
  const openEditComp = (row: BaseRow) => { setEditingCompId(row.id); setECompTitle(row.title || ''); setECompActive(!!row.isActive); };
  const cancelEditComp = () => { setEditingCompId(''); setECompTitle(''); setECompActive(false); };
  const onSaveComp = async (id: string) => {
    if (!eCompTitle.trim()) { setError('Lütfen refakatçi başlığı girin'); return; }
    try {
      setSavingComp(true); setError('');
      const body: any = { title: eCompTitle.trim(), isActive: !!eCompActive };
      const res = await fetch(toUrl(`/api/admin/companions/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Refakatçi güncelleme başarısız');
      cancelEditComp(); await fetchCompanions();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingComp(false); }
  };

  const openEditVis = (row: BaseRow) => { setEditingVisId(row.id); setEVisTitle(row.title || ''); setEVisActive(!!row.isActive); };
  const cancelEditVis = () => { setEditingVisId(''); setEVisTitle(''); setEVisActive(false); };
  const onSaveVis = async (id: string) => {
    if (!eVisTitle.trim()) { setError('Lütfen ziyaretçi başlığı girin'); return; }
    try {
      setSavingVis(true); setError('');
      const body: any = { title: eVisTitle.trim(), isActive: !!eVisActive };
      const res = await fetch(toUrl(`/api/admin/visitors/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Ziyaretçi güncelleme başarısız');
      cancelEditVis(); await fetchVisitors();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingVis(false); }
  };

  // Delete handlers
  const onDeleteComp = (id: string) => setConfirmCompId(id);
  const confirmDeleteComp = async () => {
    if (!confirmCompId) return;
    try {
      setConfirmCompLoading(true);
      const res = await fetch(toUrl(`/api/admin/companions/${confirmCompId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Refakatçi silme başarısız');
      setConfirmCompId(''); await fetchCompanions();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setConfirmCompLoading(false); }
  };

  const onDeleteVis = (id: string) => setConfirmVisId(id);
  const confirmDeleteVis = async () => {
    if (!confirmVisId) return;
    try {
      setConfirmVisLoading(true);
      const res = await fetch(toUrl(`/api/admin/visitors/${confirmVisId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Ziyaretçi silme başarısız');
      setConfirmVisId(''); await fetchVisitors();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setConfirmVisLoading(false); }
  };

  // ================= Visitor Items (Paragraphs & List Items) =================
  type VisParagraph = { id: string; visitorId: string; orderIndex: number; text: string; isActive: boolean; updatedAt: string };
  type VisListItem = { id: string; visitorId: string; orderIndex: number; text: string; isActive: boolean; updatedAt: string };

  const [selectedVis, setSelectedVis] = React.useState<BaseRow | null>(null);
  const [visTab, setVisTab] = React.useState<'paragraphs' | 'list'>('paragraphs');

  // Visitor paragraphs state
  const [vParas, setVParas] = React.useState<VisParagraph[]>([]);
  const [loadingVParas, setLoadingVParas] = React.useState(false);
  const [cVParaText, setCVParaText] = React.useState('');
  const [cVParaOrder, setCVParaOrder] = React.useState<number>(0);
  const [cVParaActive, setCVParaActive] = React.useState(true);
  const [creatingVPara, setCreatingVPara] = React.useState(false);
  const [editingVParaId, setEditingVParaId] = React.useState('');
  const [eVParaText, setEVParaText] = React.useState('');
  const [eVParaOrder, setEVParaOrder] = React.useState<number>(0);
  const [eVParaActive, setEVParaActive] = React.useState(true);
  const [savingVPara, setSavingVPara] = React.useState(false);
  const [delVParaId, setDelVParaId] = React.useState('');
  const [delVParaLoading, setDelVParaLoading] = React.useState(false);

  // Visitor list items state
  const [vItems, setVItems] = React.useState<VisListItem[]>([]);
  const [loadingVItems, setLoadingVItems] = React.useState(false);
  const [cVItemText, setCVItemText] = React.useState('');
  const [cVItemOrder, setCVItemOrder] = React.useState<number>(0);
  const [cVItemActive, setCVItemActive] = React.useState(true);
  const [creatingVItem, setCreatingVItem] = React.useState(false);
  const [editingVItemId, setEditingVItemId] = React.useState('');
  const [eVItemText, setEVItemText] = React.useState('');
  const [eVItemOrder, setEVItemOrder] = React.useState<number>(0);
  const [eVItemActive, setEVItemActive] = React.useState(true);
  const [savingVItem, setSavingVItem] = React.useState(false);
  const [delVItemId, setDelVItemId] = React.useState('');
  const [delVItemLoading, setDelVItemLoading] = React.useState(false);

  const loadVisitorChildren = React.useCallback(async (vis: BaseRow) => {
    try {
      setLoadingVParas(true); setLoadingVItems(true);
      const [r1, r2] = await Promise.all([
        fetch(toUrl(`/api/admin/visitors/${vis.id}/paragraphs`)),
        fetch(toUrl(`/api/admin/visitors/${vis.id}/list-items`)),
      ]);
      const pjs = r1.ok ? await r1.json() : [];
      const ijs = r2.ok ? await r2.json() : [];
      setVParas(pjs || []);
      setVItems(ijs || []);
    } catch (e) { /* ignore */ }
    finally { setLoadingVParas(false); setLoadingVItems(false); }
  }, []);

  const openVisContents = (row: BaseRow) => { setSelectedVis(row); setVisTab('paragraphs'); loadVisitorChildren(row); };

  // Create visitor paragraph
  const resetCreateVPara = () => { setCVParaText(''); setCVParaOrder(0); setCVParaActive(true); };
  const onCreateVPara = async (e: React.FormEvent) => {
    e.preventDefault(); if (!selectedVis) return;
    if (!cVParaText.trim()) { setError('Lütfen paragraf metni girin'); return; }
    try {
      setCreatingVPara(true); setError('');
      const body = { visitorId: selectedVis.id, orderIndex: cVParaOrder || 0, text: cVParaText.trim(), isActive: !!cVParaActive };
      const res = await fetch(toUrl(`/api/admin/visitors/${selectedVis.id}/paragraphs`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Paragraf ekleme başarısız');
      resetCreateVPara(); await loadVisitorChildren(selectedVis);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreatingVPara(false); }
  };

  const openEditVPara = (p: VisParagraph) => { setEditingVParaId(p.id); setEVParaText(p.text || ''); setEVParaOrder(p.orderIndex || 0); setEVParaActive(!!p.isActive); };
  const cancelEditVPara = () => { setEditingVParaId(''); setEVParaText(''); setEVParaOrder(0); setEVParaActive(true); };
  const onSaveVPara = async (id: string) => {
    if (!eVParaText.trim()) { setError('Lütfen paragraf metni girin'); return; }
    try {
      setSavingVPara(true); setError('');
      const body: any = { text: eVParaText.trim(), orderIndex: eVParaOrder || 0, isActive: !!eVParaActive };
      const res = await fetch(toUrl(`/api/admin/visitor-paragraphs/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Paragraf güncelleme başarısız');
      cancelEditVPara(); if (selectedVis) await loadVisitorChildren(selectedVis);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingVPara(false); }
  };
  const onDeleteVPara = (id: string) => setDelVParaId(id);
  const confirmDeleteVPara = async () => {
    if (!delVParaId) return;
    try {
      setDelVParaLoading(true);
      const res = await fetch(toUrl(`/api/admin/visitor-paragraphs/${delVParaId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Paragraf silme başarısız');
      setDelVParaId(''); if (selectedVis) await loadVisitorChildren(selectedVis);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelVParaLoading(false); }
  };

  // Create visitor list item
  const resetCreateVItem = () => { setCVItemText(''); setCVItemOrder(0); setCVItemActive(true); };
  const onCreateVItem = async (e: React.FormEvent) => {
    e.preventDefault(); if (!selectedVis) return;
    if (!cVItemText.trim()) { setError('Lütfen liste maddesi metni girin'); return; }
    try {
      setCreatingVItem(true); setError('');
      const body = { visitorId: selectedVis.id, orderIndex: cVItemOrder || 0, text: cVItemText.trim(), isActive: !!cVItemActive };
      const res = await fetch(toUrl(`/api/admin/visitors/${selectedVis.id}/list-items`), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Liste maddesi ekleme başarısız');
      resetCreateVItem(); await loadVisitorChildren(selectedVis);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreatingVItem(false); }
  };

  const openEditVItem = (it: VisListItem) => { setEditingVItemId(it.id); setEVItemText(it.text || ''); setEVItemOrder(it.orderIndex || 0); setEVItemActive(!!it.isActive); };
  const cancelEditVItem = () => { setEditingVItemId(''); setEVItemText(''); setEVItemOrder(0); setEVItemActive(true); };
  const onSaveVItem = async (id: string) => {
    if (!eVItemText.trim()) { setError('Lütfen liste maddesi metni girin'); return; }
    try {
      setSavingVItem(true); setError('');
      const body: any = { text: eVItemText.trim(), orderIndex: eVItemOrder || 0, isActive: !!eVItemActive };
      const res = await fetch(toUrl(`/api/admin/visitor-list-items/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Liste maddesi güncelleme başarısız');
      cancelEditVItem(); if (selectedVis) await loadVisitorChildren(selectedVis);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSavingVItem(false); }
  };
  const onDeleteVItem = (id: string) => setDelVItemId(id);
  const confirmDeleteVItem = async () => {
    if (!delVItemId) return;
    try {
      setDelVItemLoading(true);
      const res = await fetch(toUrl(`/api/admin/visitor-list-items/${delVItemId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Liste maddesi silme başarısız');
      setDelVItemId(''); if (selectedVis) await loadVisitorChildren(selectedVis);
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelVItemLoading(false); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Refakatçi & Ziyaretçi</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Refakatçi: {companions.length} • Ziyaretçi: {visitors.length}</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button onClick={() => setTab('companion')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: tab === 'companion' ? '#2563eb' : '#fff', color: tab === 'companion' ? '#fff' : '#2563eb', cursor: 'pointer' }}>Refakatçi</button>
        <button onClick={() => setTab('visitor')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: tab === 'visitor' ? '#2563eb' : '#fff', color: tab === 'visitor' ? '#fff' : '#2563eb', cursor: 'pointer' }}>Ziyaretçi</button>
      </div>

      {tab === 'companion' && (
        <div>
          {/* Create Comp */}
          {createCompOpen && (
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Yeni Refakatçi Ekle</h3>
                <button type="button" onClick={() => setCreateCompOpen(false)} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
              <form onSubmit={onCreateComp} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="form-label">Başlık</label>
                  <input type="text" className="input" value={cCompTitle} onChange={(e) => setCCompTitle(e.target.value)} required />
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="form-label">Aktif</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input id="cCompActive" type="checkbox" checked={cCompActive} onChange={(e) => setCCompActive(e.target.checked)} />
                    <label htmlFor="cCompActive">Aktif</label>
                  </div>
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={creatingComp} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creatingComp ? 0.8 : 1 }}>{creatingComp ? 'Ekleniyor...' : 'Ekle'}</button>
                </div>
              </form>
              {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
            </div>
          )}

          {/* List Comp */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="text" value={qComp} onChange={(e) => setQComp(e.target.value)} placeholder="Ara: başlık" className="input" style={{ width: 280 }} />
                {qComp && <button onClick={() => setQComp('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={fetchCompanions} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
                <button onClick={() => { resetCreateComp(); setCreateCompOpen(true); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer' }}>Yeni Ekle</button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Başlık</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingComp && (<tr><td colSpan={4} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                  {!loadingComp && filteredComp.length === 0 && (<tr><td colSpan={4} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                  {filteredComp.map(row => (
                    <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 16px' }}>
                        {editingCompId === row.id ? (
                          <input type="text" value={eCompTitle} onChange={(e) => setECompTitle(e.target.value)} className="input" />
                        ) : row.title}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {editingCompId === row.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input id={`cAct-${row.id}`} type="checkbox" checked={eCompActive} onChange={(e) => setECompActive(e.target.checked)} />
                            <label htmlFor={`cAct-${row.id}`}>Aktif</label>
                          </div>
                        ) : (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.isActive ? '#ecfdf5' : '#fef2f2', color: row.isActive ? '#065f46' : '#991b1b', border: `1px solid ${row.isActive ? '#a7f3d0' : '#fecaca'}` }}>{row.isActive ? 'Aktif' : 'Pasif'}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {editingCompId === row.id ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => onSaveComp(row.id)} disabled={savingComp} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingComp ? 0.8 : 1 }}>{savingComp ? 'Kaydediliyor...' : 'Kaydet'}</button>
                            <button onClick={cancelEditComp} disabled={savingComp} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => openCompContents(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0ea5e9', background: '#fff', color: '#0ea5e9', cursor: 'pointer' }}>İçerikler</button>
                            <button onClick={() => openEditComp(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                            <button onClick={() => onDeleteComp(row.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Companion children tables */}
          {selectedComp && (
            <div className="card" style={{ padding: 16, marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h4 style={{ margin: 0 }}>Seçili Refakatçi: {selectedComp.title}</h4>
                <button type="button" onClick={() => setSelectedComp(null)} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button onClick={() => setCompTab('paragraphs')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: compTab === 'paragraphs' ? '#2563eb' : '#fff', color: compTab === 'paragraphs' ? '#fff' : '#2563eb', cursor: 'pointer' }}>Paragraflar</button>
                <button onClick={() => setCompTab('list')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: compTab === 'list' ? '#2563eb' : '#fff', color: compTab === 'list' ? '#fff' : '#2563eb', cursor: 'pointer' }}>Liste Maddeleri</button>
              </div>

              {/* Paragraphs */}
              {compTab === 'paragraphs' && (
                <div>
                  <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                    <h5 style={{ margin: 0, marginBottom: 12 }}>Yeni Paragraf Ekle</h5>
                    <form onSubmit={onCreatePara} style={{ display: 'grid', gridTemplateColumns: '1fr 180px 160px', gap: 12 }}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Metin</label>
                        <textarea className="input" rows={3} value={cParaText} onChange={(e) => setCParaText(e.target.value)} required />
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Sıra</label>
                        <input type="number" className="input" value={cParaOrder} onChange={(e) => setCParaOrder(parseInt(e.target.value || '0', 10))} />
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Aktif</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input id="cParaActive" type="checkbox" checked={cParaActive} onChange={(e) => setCParaActive(e.target.checked)} />
                          <label htmlFor="cParaActive">Aktif</label>
                        </div>
                      </div>
                      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="submit" disabled={creatingPara} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creatingPara ? 0.8 : 1 }}>{creatingPara ? 'Ekleniyor...' : 'Ekle'}</button>
                      </div>
                    </form>
                  </div>

                  <div className="card" style={{ padding: 0 }}>
                    <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontWeight: 600 }}>Paragraflar</div>
                      <button onClick={() => selectedComp && loadCompanionChildren(selectedComp)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f3f4f6' }}>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                            <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingParas && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                          {!loadingParas && paras.length === 0 && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                          {paras.map(p => (
                            <tr key={p.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '12px 16px' }}>
                                {editingParaId === p.id ? (
                                  <textarea value={eParaText} onChange={(e) => setEParaText(e.target.value)} className="input" rows={3} />
                                ) : p.text}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {editingParaId === p.id ? (
                                  <input type="number" value={eParaOrder} onChange={(e) => setEParaOrder(parseInt(e.target.value || '0', 10))} className="input" />
                                ) : p.orderIndex}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {editingParaId === p.id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input id={`pAct-${p.id}`} type="checkbox" checked={eParaActive} onChange={(e) => setEParaActive(e.target.checked)} />
                                    <label htmlFor={`pAct-${p.id}`}>Aktif</label>
                                  </div>
                                ) : (
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: p.isActive ? '#ecfdf5' : '#fef2f2', color: p.isActive ? '#065f46' : '#991b1b', border: `1px solid ${p.isActive ? '#a7f3d0' : '#fecaca'}` }}>{p.isActive ? 'Aktif' : 'Pasif'}</span>
                                )}
                              </td>
                              <td style={{ padding: '12px 16px' }}>{p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '-'}</td>
                              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                {editingParaId === p.id ? (
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <button onClick={() => onSavePara(p.id)} disabled={savingPara} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingPara ? 0.8 : 1 }}>{savingPara ? 'Kaydediliyor...' : 'Kaydet'}</button>
                                    <button onClick={cancelEditPara} disabled={savingPara} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <button onClick={() => openEditPara(p)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                                    <button onClick={() => onDeletePara(p.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* List Items */}
              {compTab === 'list' && (
                <div>
                  <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                    <h5 style={{ margin: 0, marginBottom: 12 }}>Yeni Liste Maddesi Ekle</h5>
                    <form onSubmit={onCreateItem} style={{ display: 'grid', gridTemplateColumns: '1fr 180px 160px', gap: 12 }}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Metin</label>
                        <textarea className="input" rows={3} value={cItemText} onChange={(e) => setCItemText(e.target.value)} required />
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Sıra</label>
                        <input type="number" className="input" value={cItemOrder} onChange={(e) => setCItemOrder(parseInt(e.target.value || '0', 10))} />
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Aktif</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input id="cItemActive" type="checkbox" checked={cItemActive} onChange={(e) => setCItemActive(e.target.checked)} />
                          <label htmlFor="cItemActive">Aktif</label>
                        </div>
                      </div>
                      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="submit" disabled={creatingItem} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creatingItem ? 0.8 : 1 }}>{creatingItem ? 'Ekleniyor...' : 'Ekle'}</button>
                      </div>
                    </form>
                  </div>

                  <div className="card" style={{ padding: 0 }}>
                    <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontWeight: 600 }}>Liste Maddeleri</div>
                      <button onClick={() => selectedComp && loadCompanionChildren(selectedComp)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f3f4f6' }}>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                            <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingItems && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                          {!loadingItems && items.length === 0 && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                          {items.map(it => (
                            <tr key={it.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '12px 16px' }}>
                                {editingItemId === it.id ? (
                                  <textarea value={eItemText} onChange={(e) => setEItemText(e.target.value)} className="input" rows={3} />
                                ) : it.text}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {editingItemId === it.id ? (
                                  <input type="number" value={eItemOrder} onChange={(e) => setEItemOrder(parseInt(e.target.value || '0', 10))} className="input" />
                                ) : it.orderIndex}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {editingItemId === it.id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input id={`iAct-${it.id}`} type="checkbox" checked={eItemActive} onChange={(e) => setEItemActive(e.target.checked)} />
                                    <label htmlFor={`iAct-${it.id}`}>Aktif</label>
                                  </div>
                                ) : (
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: it.isActive ? '#ecfdf5' : '#fef2f2', color: it.isActive ? '#065f46' : '#991b1b', border: `1px solid ${it.isActive ? '#a7f3d0' : '#fecaca'}` }}>{it.isActive ? 'Aktif' : 'Pasif'}</span>
                                )}
                              </td>
                              <td style={{ padding: '12px 16px' }}>{it.updatedAt ? new Date(it.updatedAt).toLocaleString() : '-'}</td>
                              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                {editingItemId === it.id ? (
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <button onClick={() => onSaveItem(it.id)} disabled={savingItem} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingItem ? 0.8 : 1 }}>{savingItem ? 'Kaydediliyor...' : 'Kaydet'}</button>
                                    <button onClick={cancelEditItem} disabled={savingItem} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <button onClick={() => openEditItem(it)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                                    <button onClick={() => onDeleteItem(it.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'visitor' && (
        <div>
          {/* Create Vis */}
          {createVisOpen && (
            <div className="card" style={{ padding: 16, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h3 style={{ margin: 0 }}>Yeni Ziyaretçi Ekle</h3>
                <button type="button" onClick={() => setCreateVisOpen(false)} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
              <form onSubmit={onCreateVis} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="form-label">Başlık</label>
                  <textarea className="input" rows={3} value={cVisTitle} onChange={(e) => setCVisTitle(e.target.value)} required />
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  <label className="form-label">Aktif</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input id="cVisActive" type="checkbox" checked={cVisActive} onChange={(e) => setCVisActive(e.target.checked)} />
                    <label htmlFor="cVisActive">Aktif</label>
                  </div>
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                  <button type="submit" disabled={creatingVis} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creatingVis ? 0.8 : 1 }}>{creatingVis ? 'Ekleniyor...' : 'Ekle'}</button>
                </div>
              </form>
              {error && <div style={{ marginTop: 8, color: '#b00020' }}>{error}</div>}
            </div>
          )}

          {/* List Vis */}
          <div className="card" style={{ padding: 0 }}>
            <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="text" value={qVis} onChange={(e) => setQVis(e.target.value)} placeholder="Ara: başlık" className="input" style={{ width: 280 }} />
                {qVis && <button onClick={() => setQVis('')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Temizle</button>}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={fetchVisitors} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
                <button onClick={() => { resetCreateVis(); setCreateVisOpen(true); }} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer' }}>Yeni Ekle</button>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Başlık</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingVis && (<tr><td colSpan={4} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                  {!loadingVis && filteredVis.length === 0 && (<tr><td colSpan={4} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                  {filteredVis.map(row => (
                    <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '12px 16px' }}>
                        {editingVisId === row.id ? (
                          <textarea value={eVisTitle} onChange={(e) => setEVisTitle(e.target.value)} className="input" rows={3} />
                        ) : row.title}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {editingVisId === row.id ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input id={`vAct-${row.id}`} type="checkbox" checked={eVisActive} onChange={(e) => setEVisActive(e.target.checked)} />
                            <label htmlFor={`vAct-${row.id}`}>Aktif</label>
                          </div>
                        ) : (
                          <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.isActive ? '#ecfdf5' : '#fef2f2', color: row.isActive ? '#065f46' : '#991b1b', border: `1px solid ${row.isActive ? '#a7f3d0' : '#fecaca'}` }}>{row.isActive ? 'Aktif' : 'Pasif'}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                        {editingVisId === row.id ? (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            <button onClick={() => onSaveVis(row.id)} disabled={savingVis} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingVis ? 0.8 : 1 }}>{savingVis ? 'Kaydediliyor...' : 'Kaydet'}</button>
                            <button onClick={cancelEditVis} disabled={savingVis} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => openVisContents(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #0ea5e9', background: '#fff', color: '#0ea5e9', cursor: 'pointer' }}>İçerikler</button>
                            <button onClick={() => openEditVis(row)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                            <button onClick={() => onDeleteVis(row.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Visitor children tables */}
          {selectedVis && (
            <div className="card" style={{ padding: 16, marginTop: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h4 style={{ margin: 0 }}>Seçili Ziyaretçi: {selectedVis.title}</h4>
                <button type="button" onClick={() => setSelectedVis(null)} aria-label="Kapat" style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button onClick={() => setVisTab('paragraphs')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: visTab === 'paragraphs' ? '#2563eb' : '#fff', color: visTab === 'paragraphs' ? '#fff' : '#2563eb', cursor: 'pointer' }}>Paragraflar</button>
                <button onClick={() => setVisTab('list')} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: visTab === 'list' ? '#2563eb' : '#fff', color: visTab === 'list' ? '#fff' : '#2563eb', cursor: 'pointer' }}>Liste Maddeleri</button>
              </div>

              {/* Visitor Paragraphs */}
              {visTab === 'paragraphs' && (
                <div>
                  <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                    <h5 style={{ margin: 0, marginBottom: 12 }}>Yeni Paragraf Ekle</h5>
                    <form onSubmit={onCreateVPara} style={{ display: 'grid', gridTemplateColumns: '1fr 180px 160px', gap: 12 }}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Metin</label>
                        <textarea className="input" rows={3} value={cVParaText} onChange={(e) => setCVParaText(e.target.value)} required />
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Sıra</label>
                        <input type="number" className="input" value={cVParaOrder} onChange={(e) => setCVParaOrder(parseInt(e.target.value || '0', 10))} />
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Aktif</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input id="cVParaActive" type="checkbox" checked={cVParaActive} onChange={(e) => setCVParaActive(e.target.checked)} />
                          <label htmlFor="cVParaActive">Aktif</label>
                        </div>
                      </div>
                      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="submit" disabled={creatingVPara} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creatingVPara ? 0.8 : 1 }}>{creatingVPara ? 'Ekleniyor...' : 'Ekle'}</button>
                      </div>
                    </form>
                  </div>

                  <div className="card" style={{ padding: 0 }}>
                    <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontWeight: 600 }}>Paragraflar</div>
                      <button onClick={() => selectedVis && loadVisitorChildren(selectedVis)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f3f4f6' }}>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                            <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingVParas && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                          {!loadingVParas && vParas.length === 0 && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                          {vParas.map(p => (
                            <tr key={p.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '12px 16px' }}>
                                {editingVParaId === p.id ? (
                                  <textarea value={eVParaText} onChange={(e) => setEVParaText(e.target.value)} className="input" rows={3} />
                                ) : p.text}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {editingVParaId === p.id ? (
                                  <input type="number" value={eVParaOrder} onChange={(e) => setEVParaOrder(parseInt(e.target.value || '0', 10))} className="input" />
                                ) : p.orderIndex}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {editingVParaId === p.id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input id={`vpAct-${p.id}`} type="checkbox" checked={eVParaActive} onChange={(e) => setEVParaActive(e.target.checked)} />
                                    <label htmlFor={`vpAct-${p.id}`}>Aktif</label>
                                  </div>
                                ) : (
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: p.isActive ? '#ecfdf5' : '#fef2f2', color: p.isActive ? '#065f46' : '#991b1b', border: `1px solid ${p.isActive ? '#a7f3d0' : '#fecaca'}` }}>{p.isActive ? 'Aktif' : 'Pasif'}</span>
                                )}
                              </td>
                              <td style={{ padding: '12px 16px' }}>{p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '-'}</td>
                              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                {editingVParaId === p.id ? (
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <button onClick={() => onSaveVPara(p.id)} disabled={savingVPara} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingVPara ? 0.8 : 1 }}>{savingVPara ? 'Kaydediliyor...' : 'Kaydet'}</button>
                                    <button onClick={cancelEditVPara} disabled={savingVPara} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <button onClick={() => openEditVPara(p)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                                    <button onClick={() => onDeleteVPara(p.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Visitor List Items */}
              {visTab === 'list' && (
                <div>
                  <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                    <h5 style={{ margin: 0, marginBottom: 12 }}>Yeni Liste Maddesi Ekle</h5>
                    <form onSubmit={onCreateVItem} style={{ display: 'grid', gridTemplateColumns: '1fr 180px 160px', gap: 12 }}>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Metin</label>
                        <textarea className="input" rows={3} value={cVItemText} onChange={(e) => setCVItemText(e.target.value)} required />
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Sıra</label>
                        <input type="number" className="input" value={cVItemOrder} onChange={(e) => setCVItemOrder(parseInt(e.target.value || '0', 10))} />
                      </div>
                      <div style={{ display: 'grid', gap: 6 }}>
                        <label className="form-label">Aktif</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input id="cVItemActive" type="checkbox" checked={cVItemActive} onChange={(e) => setCVItemActive(e.target.checked)} />
                          <label htmlFor="cVItemActive">Aktif</label>
                        </div>
                      </div>
                      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                        <button type="submit" disabled={creatingVItem} style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: creatingVItem ? 0.8 : 1 }}>{creatingVItem ? 'Ekleniyor...' : 'Ekle'}</button>
                      </div>
                    </form>
                  </div>

                  <div className="card" style={{ padding: 0 }}>
                    <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ fontWeight: 600 }}>Liste Maddeleri</div>
                      <button onClick={() => selectedVis && loadVisitorChildren(selectedVis)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>Yenile</button>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f3f4f6' }}>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                            <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                            <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingVItems && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
                          {!loadingVItems && vItems.length === 0 && (<tr><td colSpan={5} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
                          {vItems.map(it => (
                            <tr key={it.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '12px 16px' }}>
                                {editingVItemId === it.id ? (
                                  <textarea value={eVItemText} onChange={(e) => setEVItemText(e.target.value)} className="input" rows={3} />
                                ) : it.text}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {editingVItemId === it.id ? (
                                  <input type="number" value={eVItemOrder} onChange={(e) => setEVItemOrder(parseInt(e.target.value || '0', 10))} className="input" />
                                ) : it.orderIndex}
                              </td>
                              <td style={{ padding: '12px 16px' }}>
                                {editingVItemId === it.id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input id={`viAct-${it.id}`} type="checkbox" checked={eVItemActive} onChange={(e) => setEVItemActive(e.target.checked)} />
                                    <label htmlFor={`viAct-${it.id}`}>Aktif</label>
                                  </div>
                                ) : (
                                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: it.isActive ? '#ecfdf5' : '#fef2f2', color: it.isActive ? '#065f46' : '#991b1b', border: `1px solid ${it.isActive ? '#a7f3d0' : '#fecaca'}` }}>{it.isActive ? 'Aktif' : 'Pasif'}</span>
                                )}
                              </td>
                              <td style={{ padding: '12px 16px' }}>{it.updatedAt ? new Date(it.updatedAt).toLocaleString() : '-'}</td>
                              <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                {editingVItemId === it.id ? (
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                    <button onClick={() => onSaveVItem(it.id)} disabled={savingVItem} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #16a34a', background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: savingVItem ? 0.8 : 1 }}>{savingVItem ? 'Kaydediliyor...' : 'Kaydet'}</button>
                                    <button onClick={cancelEditVItem} disabled={savingVItem} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                    <button onClick={() => openEditVItem(it)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb', background: '#fff', color: '#2563eb', cursor: 'pointer' }}>Düzenle</button>
                                    <button onClick={() => onDeleteVItem(it.id)} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer' }}>Sil</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirm Modal (Companion) */}
      {confirmCompId && (
        <div role="dialog" aria-modal="true" aria-labelledby="compDelTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setConfirmCompId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setConfirmCompId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="compDelTitle" style={{ margin: 0, fontSize: 18 }}>Silme Onayı (Refakatçi)</h5>
              <button type="button" aria-label="Close" onClick={() => setConfirmCompId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu refakatçi kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmCompId('')} disabled={confirmCompLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
              <button type="button" onClick={confirmDeleteComp} disabled={confirmCompLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: confirmCompLoading ? 0.8 : 1 }}>{confirmCompLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal (Visitor) */}
      {confirmVisId && (
        <div role="dialog" aria-modal="true" aria-labelledby="visDelTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setConfirmVisId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setConfirmVisId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="visDelTitle" style={{ margin: 0, fontSize: 18 }}>Silme Onayı (Ziyaretçi)</h5>
              <button type="button" aria-label="Close" onClick={() => setConfirmVisId('')} style={{ background: 'transparent', border: 0, fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>Bu ziyaretçi kaydını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setConfirmVisId('')} disabled={confirmVisLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', cursor: 'pointer' }}>İptal</button>
              <button type="button" onClick={confirmDeleteVis} disabled={confirmVisLoading} style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #dc2626', background: '#dc2626', color: '#fff', cursor: 'pointer', opacity: confirmVisLoading ? 0.8 : 1 }}>{confirmVisLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanionVisitorYonetim;
